import { AppRoute } from 'appRoutes'
import { DistributionExportButton } from 'components/ChartExport'
import { LinkButtonLink } from 'components/compass-integration'
import { Distribution } from 'features/commonUI'
import { SearchIcon } from 'lucide-react'
import { DateTime } from 'luxon'
import { ComponentType, ReactNode, useCallback, useMemo } from 'react'

import {
  getAIBotLogo,
  getBotCategoryDisplayLabel,
  getBotCategoryIcon,
  getBotCategoryKey,
  getBotProviderLabel,
  getBotProviderLogo,
} from '../consts'
import {
  BotDetectionPredefinedDateRangeId,
  isEdgePredefinedDateRangeId,
  isIdentificationPredefinedDateRangeId,
} from '../dateRanges'
import { BotDetectionDistributionsResponse, BotDetectionSource, DistributionItem } from '../models'
import { BotDetectionOverviewFilterValues } from './useBotDetectionOverviewFilter'

const ROW_PROPS = {
  background: 'hsl(var(--fpds-color-gray-2))',
  labelColor: 'hsl(var(--fpds-color-gray-10))',
} as const

type BotDetectionEventsLinkSearchParam = 'botName' | 'provider' | 'category'

type BotDetectionDistributionListProps = {
  title: string
  info: string
  dimension: string
  items?: DistributionItem[]
  totalCount?: number
  isError?: boolean
  renderPrefix?: (label: string) => ReactNode
  getDisplayLabel?: (label: string) => string
  getFilterValue?: (label: string) => string
  actionSearchParam?: BotDetectionEventsLinkSearchParam
  subscriptionId: string
  since?: string
  before?: string
  period?: BotDetectionPredefinedDateRangeId
  filterValues?: Partial<BotDetectionOverviewFilterValues>
  source?: BotDetectionSource
  filtersApplied?: boolean
  from?: DateTime
  to?: DateTime
}

function BotDetectionDistributionList({
  title,
  info,
  dimension,
  items,
  totalCount,
  isError,
  renderPrefix,
  getDisplayLabel,
  getFilterValue,
  actionSearchParam,
  subscriptionId,
  since,
  before,
  period,
  filterValues,
  source,
  filtersApplied,
  from,
  to,
}: BotDetectionDistributionListProps) {
  const data = useMemo(
    () =>
      items?.map((item) => ({
        label: getDisplayLabel ? getDisplayLabel(item.label) : item.label,
        filterValue: getFilterValue ? getFilterValue(item.label) : item.label,
        totalEvents: item.count.toString(),
        share: item.share,
      })),
    [items, getDisplayLabel, getFilterValue]
  )

  const { environmentIds, ...botTaxonomyFilters } = filterValues ?? {}

  const renderAction = actionSearchParam
    ? (filterValue: string) => (
        <LinkButtonLink
          to={source === 'identification' ? AppRoute.IdentificationEvents : AppRoute.BotDetectionEvents}
          params={{ subscriptionId }}
          search={
            source === 'identification'
              ? {
                  ...(isIdentificationPredefinedDateRangeId(period) ? { period } : { since, before }),
                  ...botTaxonomyFilters,
                  ...(environmentIds ? { environment: environmentIds } : {}),
                  bot: 'all',
                  [actionSearchParam]: [filterValue],
                }
              : {
                  ...(isEdgePredefinedDateRangeId(period) ? { period } : { since, before, aggregateRange: true }),
                  ...filterValues,
                  source: 'edge',
                  botDetected: true,
                  [actionSearchParam]: [filterValue],
                }
          }
          variant='secondary'
          size='sm'
          isIcon
          aria-label={source === 'identification' ? 'Open in identification events' : 'Open in events'}
          // Prevent tooltip from persisting when tooltip is hovered
          tooltipProps={{
            className: 'pointer-events-none',
          }}
        >
          <SearchIcon />
        </LinkButtonLink>
      )
    : undefined

  return (
    <Distribution.Root>
      <Distribution.Header
        title={title}
        info={info}
        actions={
          <DistributionExportButton
            page='bot-detection'
            widget={title}
            dimension={dimension}
            hasData={!!items?.length && totalCount != null}
            getRows={() =>
              (items ?? []).map((item) => ({ dimensionValue: item.label, count: item.count, percentage: item.share }))
            }
            totalCount={totalCount}
            filtersApplied={filtersApplied}
            from={from}
            to={to}
          />
        }
      />
      <Distribution.Body isError={isError} isEmpty={data?.length === 0}>
        <Distribution.RowList
          data={data}
          labelKey='label'
          percentageKey='share'
          renderRow={(rowProps) => {
            const filterValue = (rowProps.raw.filterValue as string | undefined) ?? rowProps.label

            return (
              <Distribution.Row
                key={rowProps.index}
                index={rowProps.index}
                label={rowProps.label}
                value={rowProps.value}
                percentage={rowProps.percentage}
                background={ROW_PROPS.background}
                labelColor={ROW_PROPS.labelColor}
                valueClassName='font-mono font-normal text-gray-800'
                prefix={filterValue ? renderPrefix?.(filterValue) : null}
              >
                {filterValue ? renderAction?.(filterValue) : null}
              </Distribution.Row>
            )
          }}
        />
      </Distribution.Body>
    </Distribution.Root>
  )
}

function BrandLogo({ Logo }: { Logo?: ComponentType<{ size?: number | string }> }) {
  if (!Logo) {
    return null
  }

  return <Logo size={16} />
}

function CategoryIcon({ label }: { label: string }) {
  const Icon = getBotCategoryIcon(label)

  if (!Icon) {
    return null
  }

  return <Icon size={16} className='text-gray-700' />
}

export function BotDetectionDistributions({
  distributions,
  totalDetections,
  isLoading,
  isError,
  subscriptionId,
  since,
  before,
  period,
  filterValues,
  source,
  filtersApplied,
  from,
  to,
}: {
  distributions?: BotDetectionDistributionsResponse
  totalDetections?: number
  isLoading: boolean
  isError?: boolean
  subscriptionId: string
  since?: string
  before?: string
  period?: BotDetectionPredefinedDateRangeId
  filterValues?: Partial<BotDetectionOverviewFilterValues>
  source?: BotDetectionSource
  filtersApplied?: boolean
  from?: DateTime
  to?: DateTime
}) {
  const renderProviderLogo = useCallback((provider: string) => <BrandLogo Logo={getBotProviderLogo(provider)} />, [])
  const renderBotNameLogo = useCallback((name: string) => <BrandLogo Logo={getAIBotLogo({ name })} />, [])
  const renderCategoryIcon = useCallback((label: string) => <CategoryIcon label={label} />, [])
  const data = isLoading ? undefined : distributions

  return (
    <section className='flex flex-col gap-4' aria-label='Distributions'>
      <div className='grid gap-4 lg:grid-cols-3'>
        <BotDetectionDistributionList
          title='Top bot names'
          info='The individual bots detected most often for the selected filters.'
          dimension='bot-name'
          items={data?.topBotNames}
          totalCount={totalDetections}
          isError={isError}
          renderPrefix={renderBotNameLogo}
          actionSearchParam='botName'
          subscriptionId={subscriptionId}
          since={since}
          before={before}
          period={period}
          filterValues={filterValues}
          source={source}
          filtersApplied={filtersApplied}
          from={from}
          to={to}
        />
        <BotDetectionDistributionList
          title='Top providers'
          info='The bot providers generating the most detections for the selected filters.'
          dimension='provider'
          items={data?.topProviders}
          totalCount={totalDetections}
          isError={isError}
          renderPrefix={renderProviderLogo}
          getDisplayLabel={getBotProviderLabel}
          actionSearchParam='provider'
          subscriptionId={subscriptionId}
          since={since}
          before={before}
          period={period}
          filterValues={filterValues}
          source={source}
          filtersApplied={filtersApplied}
          from={from}
          to={to}
        />
        <BotDetectionDistributionList
          title='Top categories'
          info='The most common bot categories detected for the selected filters.'
          dimension='category'
          items={data?.topCategories}
          totalCount={totalDetections}
          isError={isError}
          renderPrefix={renderCategoryIcon}
          getDisplayLabel={getBotCategoryDisplayLabel}
          getFilterValue={getBotCategoryKey}
          actionSearchParam='category'
          subscriptionId={subscriptionId}
          since={since}
          before={before}
          period={period}
          filterValues={filterValues}
          source={source}
          filtersApplied={filtersApplied}
          from={from}
          to={to}
        />
      </div>
    </section>
  )
}
